import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

const en = {
  nav: { home: "Home", menu: "Menu", order: "Order", gallery: "Gallery", reserve: "Reserve", contact: "Contact", reviews: "Reviews", blog: "Blog", book: "Book Table", admin: "Admin" },
  common: { loading: "Loading...", cancel: "Cancel", save: "Save", delete: "Delete", edit: "Edit", confirm: "Confirm", close: "Close", back: "Back", viewAll: "View All", learnMore: "Learn More", language: "Language" },
  home: {
    welcomeTo: "Welcome to", subtitle: "Authentic Indian Restaurant · Taichung",
    viewMenu: "View Menu", orderAtTable: "Order at Table", reserveTable: "Reserve Table",
    ourPromise: "Our Promise", promiseTitle: "Soul of India, on your plate",
    chefSelection: "Chef's Selection", signatureDishes: "Signature Dishes",
    moments: "Moments", gallery: "Gallery",
    kindWords: "Kind Words", guestReviews: "Guest Reviews",
    seeGoogleReviews: "See Google Reviews", shareExperience: "Share your experience →",
    exploreMenu: "Explore Full Menu", viewFullGallery: "View Full Gallery",
    promiseCard1Title: "Fresh Spices", promiseCard1Desc: "Hand-blended masalas, ground fresh daily",
    promiseCard2Title: "Made with Love", promiseCard2Desc: "Family recipes passed through generations",
    promiseCard3Title: "Tandoor Fired", promiseCard3Desc: "Authentic clay oven for smoky perfection",
    fromGoogle: "From Google", directOnSite: "Direct on our site",
  },
  menu: {
    title: "À La Carte", subtitle: "Our Menu",
    intro: "Discover our handcrafted dishes — from royal kebabs to soulful curries.",
    search: "Search dishes...", vegOnly: "Veg only", veg: "Veg", nonVeg: "Non-Veg",
    add: "Add", noItems: "No dishes match your filters.",
    popular: "Popular", spicy: "Spicy",
    categories: {
      all: "All", soups: "Soups", starters: "Starters & Tandoor",
      specials: "Tulsi Specials", mains: "Curries", biryani: "Biryani",
      breads: "Naan & Breads", rice: "Rice", desserts: "Desserts", drinks: "Drinks",
    },
  },
  order: {
    tableService: "Table Service", title: "Order at Table",
    intro: "Scan the QR on your table or enter your table number below.",
    tableHash: "Table #", tablePh: "e.g. 12", items: "items",
    menu: "Menu", yourOrder: "Your Order",
    cartEmpty: "Your cart is empty. Tap + on a dish to add.",
    namePh: "Your name (optional)", emailPh: "Email (optional, for receipt)",
    notesPh: "Special instructions (allergies, spice level...)",
    subtotal: "Subtotal", total: "Total", cashOnly: "Cash on pickup only",
    placeOrder: "Place Order", placing: "Placing...",
    orderPlaced: "Order Placed!", onItsWay: "Your order is on its way to the kitchen.",
    table: "Table", totalCash: "Total (Cash on pickup)", cashCounter: "Cash payment at counter",
    placeAnother: "Place Another Order", backHome: "Back to Home",
    editWindow: "You can edit or cancel for {{seconds}}",
    editOrder: "Edit / Cancel Order", cancelOrder: "Cancel Order",
    printReceipt: "Print Receipt", chefReceipt: "Kitchen Receipt",
  },
  reserve: {
    title: "Book your seat", subtitle: "Reserve a Table",
    closedNote: "We are closed on Tuesdays. Open Wed–Mon.",
    pickDate: "Pick a date", pickTime: "Pick a time", yourDetails: "Your details",
    fullName: "Full name", phone: "Phone", email: "Email (optional)",
    partySize: "Party size", specialReq: "Special requests (allergies, occasion...)",
    submit: "Request Reservation", sending: "Sending...",
    confirmed: "Reservation Received", willConfirm: "We'll confirm your table shortly. A confirmation will be sent via phone.",
    name: "Name", date: "Date", guests: "Guests", makeAnother: "Make Another",
  },
  contact: {
    title: "Visit Tulsi", subtitle: "Get in Touch",
    reachUs: "Reach Us", connect: "Connect with us",
    phone: "Phone", email: "Email", address: "Address", hours: "Hours",
    closedTuesdays: "Closed on Tuesdays", comingSoon: "Social links coming soon.",
    findUs: "Find us", getDirections: "Get Directions",
  },
  reviews: {
    title: "Reviews", subtitle: "Guest Voices",
    share: "Share your experience", yourName: "Your name", rating: "Rating:",
    tellUs: "Tell us about your visit...", submit: "Submit Review",
    noReviews: "No reviews yet. Be the first!",
    thankYou: "Thank you! Your review will appear after moderation.",
  },
  gallery: { title: "Gallery", subtitle: "Moments at Tulsi", intro: "A glimpse into our kitchens, plates, and royal interiors." },
  blog: {
    title: "Stories from Tulsi",
    subtitle: "Our Journal",
    intro: "Recipes, spice notes, kitchen stories and the people behind every plate.",
    readMore: "Read More",
    backToBlog: "← Back to journal",
    by: "By",
    noPosts: "Stories coming soon. Stay tuned.",
  },
  footer: { explore: "Explore", visit: "Visit", hours: "Hours", closedTuesdays: "Closed on Tuesdays", adminPortal: "Admin Portal", copyright: "Crafted with love & saffron" },
  days: { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday", closed: "Closed" },
};

const zhTW = {
  nav: { home: "首頁", menu: "菜單", order: "點餐", gallery: "相簿", reserve: "訂位", contact: "聯絡", reviews: "評論", blog: "杜爾希誌", book: "立即訂位", admin: "管理員" },
  common: { loading: "載入中...", cancel: "取消", save: "儲存", delete: "刪除", edit: "編輯", confirm: "確認", close: "關閉", back: "返回", viewAll: "全部顯示", learnMore: "了解更多", language: "語言" },
  home: {
    welcomeTo: "歡迎光臨", subtitle: "正宗印度餐廳 · 台中",
    viewMenu: "查看菜單", orderAtTable: "桌邊點餐", reserveTable: "訂位",
    ourPromise: "我們的承諾", promiseTitle: "盤中盛載印度靈魂",
    chefSelection: "主廚精選", signatureDishes: "招牌料理",
    moments: "杜爾希時刻", gallery: "相簿",
    kindWords: "顧客好評", guestReviews: "客人評價",
    seeGoogleReviews: "查看 Google 評論", shareExperience: "分享您的體驗 →",
    exploreMenu: "完整菜單", viewFullGallery: "完整相簿",
    promiseCard1Title: "新鮮香料", promiseCard1Desc: "每日現磨手調印度馬薩拉",
    promiseCard2Title: "用愛烹煮", promiseCard2Desc: "祖傳家庭食譜世代相傳",
    promiseCard3Title: "烤爐窯燒", promiseCard3Desc: "正宗陶土烤爐 煙燻完美",
    fromGoogle: "來自 Google", directOnSite: "本站評論",
  },
  menu: {
    title: "單點菜單", subtitle: "我們的菜單",
    intro: "探索我們的手工料理 — 從皇家烤肉到濃郁咖哩。",
    search: "搜尋餐點...", vegOnly: "僅素食", veg: "素", nonVeg: "葷",
    add: "加入", noItems: "找不到符合的餐點。",
    popular: "熱門", spicy: "辛辣",
    categories: {
      all: "全部", soups: "湯品", starters: "前菜與烤爐",
      specials: "杜爾希精選", mains: "咖哩", biryani: "印度香飯",
      breads: "印度烤餅", rice: "白飯", desserts: "甜點", drinks: "飲品",
    },
  },
  order: {
    tableService: "桌邊服務", title: "桌邊點餐",
    intro: "掃描桌上 QR 碼或在下方輸入桌號。",
    tableHash: "桌號", tablePh: "例如 12", items: "項",
    menu: "菜單", yourOrder: "您的訂單",
    cartEmpty: "購物車是空的。請點擊餐點旁的 + 加入訂單。",
    namePh: "您的姓名(選填)", emailPh: "電子郵件(選填,接收收據)",
    notesPh: "特殊需求(過敏、辣度...)",
    subtotal: "小計", total: "總計", cashOnly: "僅收現金(取餐時付款)",
    placeOrder: "送出訂單", placing: "送出中...",
    orderPlaced: "訂單已送出!", onItsWay: "您的訂單已送往廚房。",
    table: "桌號", totalCash: "總計(現金結帳)", cashCounter: "請至櫃台以現金付款",
    placeAnother: "再點一單", backHome: "回首頁",
    editWindow: "您可在 {{seconds}} 內修改或取消訂單",
    editOrder: "編輯 / 取消訂單", cancelOrder: "取消訂單",
    printReceipt: "列印收據", chefReceipt: "廚房收據",
  },
  reserve: {
    title: "預約您的座位", subtitle: "餐廳訂位",
    closedNote: "週二公休。週三至週一營業。",
    pickDate: "選擇日期", pickTime: "選擇時間", yourDetails: "您的資料",
    fullName: "姓名", phone: "電話", email: "電子郵件(選填)",
    partySize: "人數", specialReq: "特殊需求(過敏、慶生...)",
    submit: "送出訂位", sending: "送出中...",
    confirmed: "訂位已收到", willConfirm: "我們將盡快確認您的訂位,並透過電話通知。",
    name: "姓名", date: "日期", guests: "人數", makeAnother: "再訂一桌",
  },
  contact: {
    title: "光臨杜爾希", subtitle: "聯絡我們",
    reachUs: "聯繫方式", connect: "追蹤我們",
    phone: "電話", email: "電子郵件", address: "地址", hours: "營業時間",
    closedTuesdays: "週二公休", comingSoon: "社群連結即將推出。",
    findUs: "前往我們", getDirections: "查看地圖路線",
  },
  reviews: {
    title: "顧客評論", subtitle: "顧客之聲",
    share: "分享您的體驗", yourName: "您的姓名", rating: "評分:",
    tellUs: "告訴我們您的用餐感受...", submit: "送出評論",
    noReviews: "尚無評論。歡迎成為第一位!",
    thankYou: "感謝您!您的評論將於審核後顯示。",
  },
  gallery: { title: "相簿", subtitle: "杜爾希時刻", intro: "一覽我們的廚房、料理與華麗的內裝。" },
  blog: {
    title: "杜爾希誌",
    subtitle: "我們的故事",
    intro: "食譜、香料筆記、廚房點滴,以及每一道料理背後的人。",
    readMore: "閱讀全文",
    backToBlog: "← 返回部落格",
    by: "作者",
    noPosts: "故事即將上線,敬請期待。",
  },
  footer: { explore: "探索", visit: "造訪", hours: "營業時間", closedTuesdays: "週二公休", adminPortal: "管理員後台", copyright: "以愛與番紅花精心打造" },
  days: { mon: "星期一", tue: "星期二", wed: "星期三", thu: "星期四", fri: "星期五", sat: "星期六", sun: "星期日", closed: "公休" },
};

// Simplified Chinese (zh-CN)
const zhCN = {
  nav: { home: "首页", menu: "菜单", order: "点餐", gallery: "相册", reserve: "订位", contact: "联系", reviews: "评论", blog: "杜尔希志", book: "立即订位", admin: "管理员" },
  common: { loading: "载入中...", cancel: "取消", save: "保存", delete: "删除", edit: "编辑", confirm: "确认", close: "关闭", back: "返回", viewAll: "全部显示", learnMore: "了解更多", language: "语言" },
  home: {
    welcomeTo: "欢迎光临", subtitle: "正宗印度餐厅 · 台中",
    viewMenu: "查看菜单", orderAtTable: "桌边点餐", reserveTable: "订位",
    ourPromise: "我们的承诺", promiseTitle: "盘中盛载印度灵魂",
    chefSelection: "主厨精选", signatureDishes: "招牌料理",
    moments: "杜尔希时刻", gallery: "相册",
    kindWords: "顾客好评", guestReviews: "客人评价",
    seeGoogleReviews: "查看 Google 评论", shareExperience: "分享您的体验 →",
    exploreMenu: "完整菜单", viewFullGallery: "完整相册",
    promiseCard1Title: "新鲜香料", promiseCard1Desc: "每日现磨手调印度玛萨拉",
    promiseCard2Title: "用爱烹煮", promiseCard2Desc: "祖传家庭食谱世代相传",
    promiseCard3Title: "烤炉窑烧", promiseCard3Desc: "正宗陶土烤炉 烟熏完美",
    fromGoogle: "来自 Google", directOnSite: "本站评论",
  },
  menu: {
    title: "单点菜单", subtitle: "我们的菜单",
    intro: "探索我们的手工料理 — 从皇家烤肉到浓郁咖喱。",
    search: "搜索餐点...", vegOnly: "仅素食", veg: "素", nonVeg: "荤",
    add: "加入", noItems: "找不到符合的餐点。",
    popular: "热门", spicy: "辛辣",
    categories: {
      all: "全部", soups: "汤品", starters: "前菜与烤炉",
      specials: "杜尔希精选", mains: "咖喱", biryani: "印度香饭",
      breads: "印度烤饼", rice: "米饭", desserts: "甜点", drinks: "饮品",
    },
  },
  order: {
    tableService: "桌边服务", title: "桌边点餐",
    intro: "扫描桌上 QR 码或在下方输入桌号。",
    tableHash: "桌号", tablePh: "例如 12", items: "项",
    menu: "菜单", yourOrder: "您的订单",
    cartEmpty: "购物车是空的。请点击餐点旁的 + 加入订单。",
    namePh: "您的姓名(选填)", emailPh: "电子邮件(选填,接收收据)",
    notesPh: "特殊需求(过敏、辣度...)",
    subtotal: "小计", total: "总计", cashOnly: "仅收现金(取餐时付款)",
    placeOrder: "送出订单", placing: "送出中...",
    orderPlaced: "订单已送出!", onItsWay: "您的订单已送往厨房。",
    table: "桌号", totalCash: "总计(现金结账)", cashCounter: "请至柜台以现金付款",
    placeAnother: "再点一单", backHome: "回首页",
    editWindow: "您可在 {{seconds}} 内修改或取消订单",
    editOrder: "编辑 / 取消订单", cancelOrder: "取消订单",
    printReceipt: "打印收据", chefReceipt: "厨房收据",
  },
  reserve: {
    title: "预约您的座位", subtitle: "餐厅订位",
    closedNote: "周二公休。周三至周一营业。",
    pickDate: "选择日期", pickTime: "选择时间", yourDetails: "您的资料",
    fullName: "姓名", phone: "电话", email: "电子邮件(选填)",
    partySize: "人数", specialReq: "特殊需求(过敏、庆生...)",
    submit: "送出订位", sending: "送出中...",
    confirmed: "订位已收到", willConfirm: "我们将尽快确认您的订位,并通过电话通知。",
    name: "姓名", date: "日期", guests: "人数", makeAnother: "再订一桌",
  },
  contact: {
    title: "光临杜尔希", subtitle: "联系我们",
    reachUs: "联系方式", connect: "关注我们",
    phone: "电话", email: "电子邮件", address: "地址", hours: "营业时间",
    closedTuesdays: "周二公休", comingSoon: "社群链接即将推出。",
    findUs: "前往我们", getDirections: "查看地图路线",
  },
  reviews: {
    title: "顾客评论", subtitle: "顾客之声",
    share: "分享您的体验", yourName: "您的姓名", rating: "评分:",
    tellUs: "告诉我们您的用餐感受...", submit: "送出评论",
    noReviews: "尚无评论。欢迎成为第一位!",
    thankYou: "感谢您!您的评论将于审核后显示。",
  },
  gallery: { title: "相册", subtitle: "杜尔希时刻", intro: "一览我们的厨房、料理与华丽的内装。" },
  blog: {
    title: "杜尔希志",
    subtitle: "我们的故事",
    intro: "食谱、香料笔记、厨房点滴,以及每一道料理背后的人。",
    readMore: "阅读全文",
    backToBlog: "← 返回博客",
    by: "作者",
    noPosts: "故事即将上线,敬请期待。",
  },
  footer: { explore: "探索", visit: "造访", hours: "营业时间", closedTuesdays: "周二公休", adminPortal: "管理员后台", copyright: "以爱与番红花精心打造" },
  days: { mon: "星期一", tue: "星期二", wed: "星期三", thu: "星期四", fri: "星期五", sat: "星期六", sun: "星期日", closed: "公休" },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      "zh-TW": { translation: zhTW },
      "zh-CN": { translation: zhCN },
    },
    fallbackLng: "en",
    supportedLngs: ["en", "zh-TW", "zh-CN"],
    nonExplicitSupportedLngs: false,
    load: "currentOnly",
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "tulsi_lang",
    },
  });

export const LANGUAGES = [
  { code: "en", label: "English", short: "EN" },
  { code: "zh-TW", label: "繁體中文", short: "繁" },
  { code: "zh-CN", label: "简体中文", short: "简" },
];

export default i18n;
